import { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import Lenis from "lenis";
import "@/App.css";
import { ModalContext } from "@/lib/modal";
import { getMe } from "@/lib/api";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { JoinModal } from "@/components/JoinModal";
import { ValidatorModal } from "@/components/ValidatorModal";
import { Concept } from "@/sections/Concept";
import { Contributors } from "@/sections/Contributors";
import { RoadIntelligence } from "@/sections/RoadIntelligence";
import { Trust } from "@/sections/Trust";
import { Industries } from "@/sections/Industries";
import { Validators } from "@/sections/Validators";
import { Enterprise } from "@/sections/Enterprise";
import { Roadmap } from "@/sections/Roadmap";
import { Faq } from "@/sections/Faq";
import { Closing } from "@/sections/Closing";
import Portal from "@/sections/Portal";
import Admin from "@/sections/Admin";
import Invite from "@/sections/Invite";
import Privacy from "@/sections/Privacy";
import Terms from "@/sections/Terms";
import { Toaster } from "@/components/ui/sonner";

const HomePage = () => (
  <>
    <Helmet>
      <title>Synaphy - Physical World Intelligence Network</title>
      <meta name="description" content="Synaphy builds infrastructure for capturing, understanding and verifying real-world data - the intelligence layer for the physical world. Join the Founding Network." />
    </Helmet>
    <Navbar />
    <main>
      <Hero />
      <Concept />
      <Contributors />
      <RoadIntelligence />
      <Industries />
      <Trust />
      <Validators />
      <Enterprise />
      <Roadmap />
      <Faq />
      <Closing />
    </main>
  </>
);

function App() {
  const [joinOpen, setJoinOpen] = useState(false);
  const [validatorOpen, setValidatorOpen] = useState(false);
  const [referral, setReferral] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      setReferral(ref);
      if (window.location.pathname === "/") {
        window.location.assign(`/invite/${encodeURIComponent(ref.toUpperCase())}`);
      }
    }
  }, []);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    if (joinOpen || validatorOpen) window.__lenis?.stop();
    else window.__lenis?.start();
  }, [joinOpen, validatorOpen]);

  const modals = useMemo(
    () => ({
      // Every "Join the Founding Network" button routes through here. If the
      // visitor already has a live session, tell them instead of reopening signup.
      openJoin: async () => {
        try {
          const m = await getMe();
          toast.info(
            `You're already logged in${m?.name ? `, ${m.name.split(" ")[0]}` : ""} - your Founding account is active.`,
            {
              data: { testid: "already-logged-in-toast" },
              action: {
                label: "Open Portal",
                onClick: () => window.location.assign("/portal"),
              },
            }
          );
        } catch {
          setJoinOpen(true); // logged out (or transient error) -> normal signup flow
        }
      },
      openValidator: () => setValidatorOpen(true),
      setReferral,
    }),
    []
  );

  return (
    <ModalContext.Provider value={modals}>
      <HelmetProvider>
      <BrowserRouter>
        <div className="relative bg-ink-950 text-mist min-h-screen font-sans" data-testid="synaphy-app">
          <div className="aurora" aria-hidden="true" />
          <div className="noise-overlay" aria-hidden="true" />
          <div className="relative z-[1]">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/portal" element={<Portal />} />
              <Route path="/invite/:code" element={<Invite />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/syn-console-9k4x7d" element={<Admin />} />
            </Routes>
          </div>
          <JoinModal open={joinOpen} onOpenChange={setJoinOpen} referral={referral} />
          <ValidatorModal open={validatorOpen} onOpenChange={setValidatorOpen} />
          <Toaster theme="dark" position="bottom-right" />
        </div>
      </BrowserRouter>
      </HelmetProvider>
    </ModalContext.Provider>
  );
}

export default App;
